"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  ArrowRight,
  Shield,
  Star,
  Play,
  Brain,
  Mic,
  Users,
  BookOpenCheck,
  ChevronDown,
  ChevronUp,
  Zap,
  Award,
  X,
} from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { createClient } from '@/lib/supabase/client';
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil';
import TransparentCheckout from "@/components/ui/transparent-checkout";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: "easeOut" },
  }),
};

const modulos = [
  { num: "0", titulo: "Primeiros Passos - Preparando o Terreno" },
  { num: "1", titulo: "Entrevista Estruturada - Conhecendo Seu Expert" },
  { num: "2", titulo: "Segmentação de Público - Para Quem Você Ensina" },
  { num: "3", titulo: "Persona Educacional - O Aluno Ideal" },
  { num: "4", titulo: "Passaporte de Contexto - O DNA do Seu Produto" },
  { num: "5", titulo: "Arquitetura Curricular - Estruturando o Conteúdo" },
  { num: "6", titulo: "Design Instrucional - Como Ensinar de Verdade" },
  { num: "7", titulo: "Produção de Conteúdo - Criando as Aulas" },
  { num: "8", titulo: "Validação e Testes - Garantindo Qualidade" },
  { num: "9", titulo: "Lançamento e Vendas - Colocando no Mundo" },
  { num: "10", titulo: "Escala e Automação - Crescendo Sem Depender de Você" },
];

const faq = [
  {
    pergunta: "Preciso ter experiência com tecnologia?",
    resposta:
      "Não. O curso foi desenhado para experts de qualquer área. A plataforma eBuz cuida da parte técnica com ferramentas de IA que simplificam todo o processo. Você foca no seu conhecimento, a tecnologia trabalha por você.",
  },
  {
    pergunta: "Quanto tempo leva para criar meu primeiro produto?",
    resposta:
      "Depende da sua dedicação, mas alunos que seguem o ritmo sugerido conseguem ter a estrutura completa em 4 a 6 semanas. O módulo 0 já te coloca em ação no primeiro dia.",
  },
  {
    pergunta: "E se eu não gostar do curso?",
    resposta:
      "Você tem 7 dias de garantia incondicional. Se por qualquer motivo decidir que o curso não é para você, basta pedir o reembolso e devolvemos 100% do valor. Sem perguntas.",
  },
  {
    pergunta: "O acesso é vitalício?",
    resposta:
      "Sim. Uma vez inscrito, você tem acesso a todas as aulas, materiais e atualizações futuras do curso. Sem mensalidade, sem taxa extra.",
  },
  {
    pergunta: "Posso usar as ferramentas de IA incluídas?",
    resposta:
      "Sim. O curso inclui acesso ao Mapa de Contexto com 4 ferramentas de IA e a Entrevista Estruturada com o Jhow (IA). Essas ferramentas fazem parte da plataforma eBuz e estão disponíveis para alunos do curso.",
  },
];

export default function OfertaPage() {
  const [faqAberto, setFaqAberto] = useState<number | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [buyerName, setBuyerName] = useState<string | undefined>();
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>();
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>();
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>();
  const router = useRouter();

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined);
        setBuyerName(user.user_metadata?.full_name || undefined);
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name);
              if (profile.phone) setBuyerPhone(profile.phone);
              if (profile.document) setBuyerCpf(profile.document);
            }
          });
      }
    });
  }, []);

  function handleCTAClick(e: React.MouseEvent) {
    e.preventDefault();
    setShowCheckout(true);
    setCheckoutError("");
    setTimeout(() => {
      document.getElementById("checkout-section")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }

  return (
    <main className="min-h-screen bg-[#faf7f5]">
      {/* Header */}
      <header className="w-full py-4 px-6 border-b border-gray-100">
        <div className="mx-auto max-w-5xl flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-violet-600" />
          <span className="text-lg font-semibold text-gray-900">eBuz</span>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-4 pt-10 pb-12 md:pt-16 text-center">
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 px-4 py-1.5 text-sm font-medium text-violet-700 mb-6">
            <Award className="h-3.5 w-3.5" />
            Curso completo
          </span>
        </motion.div>

        <motion.h1
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          Arquitetura de Produtos Educacionais
          <br />
          <span className="text-violet-600">O Curso Completo</span>
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-10"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          De expert preso na presença a arquiteto de produtos que escalam
        </motion.p>

        {/* Author + Book mockup */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-8 mt-2"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={3}
        >
          <div className="flex items-center gap-4">
            <Image
              src="/images/jonathan-machado.jpg"
              alt="Jonathan Machado"
              width={72}
              height={72}
              className="rounded-full border-2 border-violet-200"
            />
            <div className="text-left">
              <p className="font-semibold text-gray-900">Jonathan Machado</p>
              <p className="text-sm text-gray-500">Criador da Metodologia APE</p>
            </div>
          </div>
          <Image
            src="/images/mockup-livro-ape.webp"
            alt="Livro APE - Arquitetura de Produtos Educacionais"
            width={180}
            height={240}
            className="rounded-xl shadow-lg"
          />
        </motion.div>
      </section>

      {/* O que esta incluido */}
      <section className="mx-auto max-w-4xl px-4 pb-16">
        <motion.div
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={3}
        >
          {[
            { icon: Play, texto: "67 aulas em 12 módulos" },
            { icon: Mic, texto: "Áudio profissional em todas as aulas" },
            { icon: Brain, texto: "Mapa de Contexto com IA (4 ferramentas)" },
            { icon: Zap, texto: "Entrevista Estruturada com Jhow (IA)" },
            { icon: BookOpenCheck, texto: "Acesso ao Livro APE digital" },
            { icon: Users, texto: "Comunidade de experts" },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                className="flex items-start gap-3 rounded-xl bg-white p-4 border border-gray-100"
              >
                <div className="rounded-lg bg-violet-50 p-2">
                  <Icon className="h-5 w-5 text-violet-600" />
                </div>
                <span className="text-gray-700 font-medium pt-1.5">
                  {item.texto}
                </span>
              </div>
            );
          })}
        </motion.div>
      </section>

      {/* Módulos */}
      <section className="mx-auto max-w-4xl px-4 pb-16">
        <motion.h2
          className="text-2xl font-bold text-gray-900 text-center mb-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          O que você vai aprender
        </motion.h2>

        <motion.div
          className="space-y-3"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          {modulos.map((mod, i) => (
            <div
              key={i}
              className="flex items-center gap-4 rounded-xl bg-white px-5 py-4 border border-gray-100"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-sm font-bold text-violet-700 shrink-0">
                {mod.num}
              </span>
              <span className="text-gray-700">{mod.titulo}</span>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Pricing */}
      <section className="mx-auto max-w-lg px-4 pb-16">
        <motion.div
          className="rounded-2xl bg-white border-2 border-violet-200 p-8 text-center shadow-lg"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <span className="inline-block rounded-full bg-violet-100 px-4 py-1 text-sm font-medium text-violet-700 mb-4">
            Acesso completo
          </span>

          <div className="mb-2">
            <span className="text-sm text-gray-500 line-through">
              R$ 3.000
            </span>
          </div>

          <div className="flex items-baseline justify-center gap-1 mb-1">
            <span className="text-lg text-gray-500">R$</span>
            <span className="text-5xl font-bold text-gray-900">2.000</span>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            ou 12x de R$ 197 no cartão
          </p>

          {/* Book included */}
          <div className="flex items-center gap-3 rounded-xl bg-violet-50 p-3 mb-6">
            <Image
              src="/images/capa-livro-ape.webp"
              alt="Capa do Livro APE"
              width={48}
              height={64}
              className="rounded-md shadow-sm"
            />
            <p className="text-sm text-violet-700 font-medium text-left">
              Inclui o Livro APE digital - Arquitetura de Produtos Educacionais
            </p>
          </div>

          <button
            onClick={handleCTAClick}
            className="flex items-center justify-center gap-2 w-full rounded-xl bg-violet-600 py-4 text-white font-semibold text-lg hover:bg-violet-700 active:scale-[0.98] transition-all shadow-lg shadow-violet-600/20 mb-4 cursor-pointer"
          >
            Quero Começar Agora
            <ArrowRight className="h-5 w-5" />
          </button>

          <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Shield className="h-4 w-4" />
              7 dias de garantia
            </span>
            <span className="flex items-center gap-1">
              <Star className="h-4 w-4" />
              Acesso vitalício
            </span>
          </div>
        </motion.div>

        <motion.p
          className="text-center text-sm text-gray-400 mt-4"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          Garantia incondicional de 7 dias. Se não gostar, devolvemos 100% do
          valor.
        </motion.p>
      </section>

      {/* Checkout Section */}
      {showCheckout && (
        <section id="checkout-section" className="mx-auto max-w-lg px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Finalizar Compra</h2>
              <button
                onClick={() => setShowCheckout(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            {checkoutError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {checkoutError}
              </div>
            )}

            <TransparentCheckout
              channelSlug="oferta"
              buyerName={buyerName}
              buyerEmail={buyerEmail}
              buyerPhone={buyerPhone}
              buyerCpf={buyerCpf}
              onSuccess={(paymentId) => {
                router.push("/obrigado?payment=" + paymentId);
              }}
              onError={(error) => {
                setCheckoutError(error);
              }}
            />
          </motion.div>
        </section>
      )}

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 pb-20">
        <motion.h2
          className="text-2xl font-bold text-gray-900 text-center mb-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Perguntas frequentes
        </motion.h2>

        <motion.div
          className="space-y-3"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          {faq.map((item, i) => (
            <div
              key={i}
              className="rounded-xl bg-white border border-gray-100 overflow-hidden"
            >
              <button
                onClick={() => setFaqAberto(faqAberto === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
              >
                <span className="font-medium text-gray-900">
                  {item.pergunta}
                </span>
                {faqAberto === i ? (
                  <ChevronUp className="h-5 w-5 text-gray-400 shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400 shrink-0" />
                )}
              </button>
              {faqAberto === i && (
                <div className="px-5 pb-4">
                  <p className="text-gray-600 leading-relaxed">
                    {item.resposta}
                  </p>
                </div>
              )}
            </div>
          ))}
        </motion.div>
      </section>

      {/* Footer CTA */}
      <section className="bg-violet-600 py-12 text-center">
        <div className="mx-auto max-w-2xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Pronto para começar?
          </h2>
          <p className="text-violet-200 mb-6">
            Junte-se a mais de 125 alunos que já estão construindo seus produtos
            educacionais.
          </p>
          <button
            onClick={handleCTAClick}
            className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-violet-700 font-semibold text-lg hover:bg-violet-50 active:scale-[0.98] transition-all cursor-pointer"
          >
            Quero Começar Agora
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>
    </main>
  );
}
